<template>
  <div>
    <div class="list-header">
      <b-row class="header-grid">
        <i
          v-if="!fixed && $route.path.includesLowerCase('/Client/')"
          class="nav-icon i-Add addArea"
          @click="addTask"
        ></i>
        <div class="titleArea" :class="{ 'no-add-task': notOnClientPage }">
          <input
            v-if="!fixed && list.id.includes('Quote_')"
            :id="`quoteAnchor_${list.id}`"
            ref="listName"
            v-model="list.name"
            readonly
          />
          <input
            v-else-if="!fixed"
            :id="`quoteAnchor_${list.id}`"
            ref="listName"
            v-model="list.name"
            @input="updateList"
          />
          <input v-else v-model="list.name" readonly />
        </div>
        <div class="ratioArea">
          <b-badge
            v-show="list.completedTasksRatio"
            pill
            variant="outline-info"
            class="completed-ratio"
          >
            {{ list.completedTasksRatio }}
          </b-badge>
        </div>
        <div class="dateArea">
          <v-date-picker
            v-model="dateRange"
            :first-day-of-week="1"
            mode="range"
            :popover="{ placement: 'left', visibility: 'click' }"
            class="fill"
            :masks="{ input: 'YYYY/MM/DD' }"
          >
            <b-badge
              v-if="isOverdue"
              pill
              class="overdue cursor-pointer"
              variant="danger"
              >{{ formattedDate }}</b-badge
            >
            <b-badge
              v-else-if="dateRange.start || dateRange.end"
              pill
              class="cursor-pointer"
              variant="info"
            >
              {{ formattedDate }}
            </b-badge>

            <b-badge v-else pill class="cursor-pointer" variant="info">
              Due Date
            </b-badge>
          </v-date-picker>
        </div>
      </b-row>
      <b-row class="sort-row">
        <b-col md="10" class="sort-grid">
          <i
            class="nav-icon i-Circular-Point sort-trigger numberSort"
            @click="sortTasks('number')"
          ></i>
          <i
            class="nav-icon i-Circular-Point hourSort sort-trigger"
            @click="sortTasks('hours')"
          ></i>
          <i
            class="nav-icon i-Circular-Point dateSort sort-trigger"
            @click="sortTasks('dueDate')"
          ></i>
          <i
            class="nav-icon i-Circular-Point statusSort sort-trigger"
            @click="sortTasks('status')"
          ></i>
          <i
            class="nav-icon i-Circular-Point prioritySort sort-trigger"
            @click="sortTasks('priority')"
          ></i>
        </b-col>
        <b-col md="2">
          <ApolloMutation
            :mutation="require('@/graphql/Section/DeleteSection.gql')"
            :variables="{
              section: {
                id: list.id,
                clientId: list.clientId,
                name: list.name,
                end: list.end,
                start: list.start
              }
            }"
            @done="listDeleted"
          >
            <template v-slot="{ mutate }">
              <i
                v-if="!fixed"
                v-b-popover.hover.topleft="'Archive'"
                class="nav-icon i-Remove f-r control"
                @click="archive(mutate)"
              ></i>
            </template>
          </ApolloMutation>
          <i
            v-if="list.tasks && list.tasks.length > 0"
            v-b-popover.hover.topleft="visible ? 'Collapse' : 'Reveal'"
            class="control"
            :class="`nav-icon i-Arrow-${visible ? 'Up' : 'Down'}-in-Circle f-r`"
            @click="changeListVisibility"
          ></i>
        </b-col>
      </b-row>
    </div>
    <b-collapse
      :id="`list-collapse_${list.id}`"
      :visible="visible"
      @hide="onCollapse(false)"
      @show="onCollapse(true)"
    >
      <div :id="list.id" class="list-group">
        <ListItem
          v-for="(task, index) in tasks"
          :key="task.id"
          v-model="tasks[index]"
          :client-id="list.clientId"
          :quote-id="list.quoteId"
          @update-task="updateTask"
        />
      </div>
    </b-collapse>
    <div class="list-group-item totals">
      <b-row>
        <b-col md="3">
          <b-progress
            v-if="!fixed"
            :max="list.tasks.length"
            height="10px"
            class="progress"
          >
            <b-progress-bar
              variant="success"
              class="progress-text"
              :value="completedTasks"
              :label="
                `${(
                  (completedTasks /
                    (list.tasks.length == 0 ? 1 : list.tasks.length)) *
                  100
                ).toFixed()}%`
              "
            ></b-progress-bar>
          </b-progress>
        </b-col>
        <b-col md="2">
          <b class="hours">{{ totalHours }}</b>
        </b-col>
        <b-col md="2"> </b-col>
        <b-col md="5">
          <b-badge v-if="!visible" pill variant="dark m-2 f-r total-pill"
            >Collapsed</b-badge
          >
        </b-col>
      </b-row>
    </div>
    <transition name="fade">
      <div
        v-show="trash.show"
        :id="`${list.id}-trash`"
        class="mb-20 trash-grid"
      >
        drop here to delete
      </div>
    </transition>
  </div>
</template>
<script>
import ListItem from "./listItem"
import Sortable from "sortablejs"
import { mapGetters, mapState, mapMutations } from "vuex"
import debounce from "debounce"

export default {
  name: "List",
  components: {
    ListItem
  },
  props: {
    value: {
      type: Object,
      default: null
    },
    fixed: {
      type: Boolean,
      deafult: false
    },
    reportType: {
      type: String,
      default: null
    },
    state: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      tasksAdded: 1,
      dateRange: { start: null, end: null },
      sortableRef: null,
      trashCanRef: null,
      hourSort: false,
      statusSort: false,
      numberSort: false,
      tasksProxy: [],
      trash: {
        show: false,
        timeout: null
      },
      visible: true,
      collapsedPriorDrag: false,
      first: true,
      shortTerm: new Date(+new Date() + 12096e5)
    }
  },
  computed: {
    ...mapState({
      hideCompletedTasks: state => state.taskcontrol.hideCompletedTasks,
      hideHoldTasks: state => state.taskcontrol.hideHoldTasks,
      collapseAll: state => state.taskcontrol.collapseAll,
      showShortTermTasks: state => state.taskcontrol.showShortTermTasks
    }),
    ...mapGetters({
      getCurrentUser: "getCurrentUser"
    }),
    list() {
      return this.value
    },
    //TODO: change to momentjs
    yesterday() {
      return new Date(new Date().setDate(new Date().getDate() - 1))
    },
    tasks() {
      //Each of these account for the STATE (all/tasks/tickets/overdue...)
      //..As well as the show/hide completed/onhold
      switch (this.state) {
        case "Overdue":
          return this.value.tasks.filter(
            x => !x.completed && new Date(x.end) <= this.yesterday
          )
        case "All":
        case "Tasks":
        default:
          return this.value.tasks.filter(
            x =>
              (!this.hideCompletedTasks || !x.completed) &&
              (!this.hideHoldTasks || x.status != "On Hold") &&
              (!this.showShortTermTasks || new Date(x.start) <= this.shortTerm)
          )
      }
    },
    totalHours() {
      const incompleteTasks = this.tasks?.filter(x => !x.completed && x.hours)
      if (!incompleteTasks.length) {
        return 0
      }
      //Reduce new array of all task hours
      let totalHours =
        (incompleteTasks
          .map(x => x.hours)
          ?.reduce(
            (accumulator, currentValue) =>
              parseFloat(accumulator) + parseFloat(currentValue)
          ) ??
          0) ||
        0
      if (!totalHours) return 0

      //Issue where value is a string when total hours is initially 0
      if (typeof totalHours == "string") totalHours = parseFloat(totalHours)

      let final = totalHours.toFixed(2)

      if (final == "0.00") return 0

      return final
    },
    completedTasks() {
      return this.value.tasks?.filter(task => {
        return task.completed
      }).length
    },
    isOverdue() {
      return this.dateRange.end && new Date(this.dateRange.end) <= new Date()
    },
    formattedDate() {
      if (!this.dateRange.start || !this.dateRange.end) return "Due Date"
      return `${this.$moment(this.dateRange.start).format(
        "DD MMM"
      )}-${this.$moment(this.dateRange.end).format("DD MMM")}`
    },
    notOnClientPage() {
      return this.$route.name !== "Client"
    }
  },
  watch: {
    dateRange() {
      //When this changes make an event for this project's client
      //TODO change to loading
      if (!this.first) {
        this.updateList()
      }
      this.first = false
    }
  },
  async created() {
    this.list.tasks = this.list.tasks?.sort((a, b) =>
      a.number > b.number ? 1 : -1
    )
    await this.setDateRange()
  },
  mounted() {
    setTimeout(() => {
      this.sortableRef = new Sortable(document.getElementById(this.list.id), {
        group: this.list.clientId,
        handle: ".task-number",
        forceFallback: true,
        animation: 150,
        onRemove: event => {
          //This is the event that gets triggered when a listItem gets removed from a list (deleted / change list)
          this.$emit("item-moved", {
            listFrom: event.from.id,
            listTo: event.to.id,
            taskId: event.item.id
          })
        },
        onSort: event => {
          if (event.from.id === event.to.id) {
            this.onTaskMove(event.oldIndex, event.newIndex)
          }
        },
        onStart: () => {
          this.$emit("dragging-started")
          document.body.style.cursor = "grabbing"
          this.trash.timeout = setTimeout(() => {
            this.trash.show = true
          }, 500)
        },
        onEnd: () => {
          this.$emit("dragging-stopped")
          document.body.style.cursor = "auto"
          clearTimeout(this.trash.timeout)
          this.trash.show = false
        }
      })
    }, 0)
    setTimeout(() => {
      this.trashCanRef = new Sortable(
        document.getElementById(`${this.list.id}-trash`),
        {
          group: this.list.clientId,
          animation: 150
          // onEnd: () => {
          // }
        }
      )
    }, 0)
    if (this.collapseAll) this.visible = false
    else {
      this.visible =
        this.list.tasks?.find(task => {
          return !task.completed
        }) != null || !this.list.tasks?.length
    }
  },
  methods: {
    ...mapMutations({
      setcollapseAll: "setcollapseAll"
    }),
    changeListVisibility() {
      this.visible = !this.visible
    },
    /**
     *
     * @param data
     */
    listDeleted({ data }) {
      this.$emit("remove-list", data.deleteSection)
    },
    async setDateRange() {
      this.dateRange = {
        start: this.list.start,
        end: this.list.end
      }
    },
    /**
     *
     * @param task
     */
    updateTask(task) {
      this.$emit("update-task", task)
    },
    /**
     *
     * @param task
     * @returns {boolean}
     */
    applicable(task) {
      if (this.userid) {
        if (task.users.includes(this.userid)) return true
        else return false
      }
      return true
    },
    updateList: debounce(function() {
      this.$emit("update-list", {
        id: this.list.id,
        clientId: this.list.clientId,
        name: this.list.name,
        end: this.$utils.getDate(this.dateRange.end),
        start: this.$utils.getDate(this.dateRange.start)
      })
    }, 500),
    /**
     *
     * @param isDragging
     */
    onSeperateListDragging(isDragging) {
      if (isDragging) {
        if (!this.visible) this.collapsedPriorDrag = true
        this.visible = true
      } else {
        if (this.collapsedPriorDrag) this.visible = false
        this.collapsedPriorDrag = false
      }
    },
    /**
     *
     * @returns {Promise<void>}
     */
    async addTask() {
      const resp = await this.$apollo.mutate({
        mutation: require("@/graphql/Task/AddTask.gql"),
        variables: {
          task: {
            sectionId: this.value.id,
            userId: this.getCurrentUser.id,
            number: this.getNextTaskNumber()
          }
        }
      })
      if (resp && this.tasksAdded > 1) this.tasksAdded--
    },
    /**
     *
     * @returns {number}
     */
    getNextTaskNumber() {
      const allTasks = this.$parent.$parent.$parent.lists.map(x => x.tasks)
      if (allTasks.every(x => !x.length)) return 0 + this.tasksAdded++
      return (
        Math.max(...[].concat(...allTasks).map(x => x.number)) +
        this.tasksAdded++
      )
    },
    displaySlackError(error) {
      this.$toasted.error(
        `There was an issue sending slack communications: ${error}`
      )
    },
    /**
     *
     * @param mutate
     */
    archive(mutate) {
      this.$dialog({
        title: `Delete: ${this.list?.name}`,
        content: "You're about to delete this list?",
        btns: [
          {
            label: "Go ahead",
            color: "#3f51b5",
            callback: async () => {
              let canDelete = await this.$apollo.query({
                query: require("@/graphql/Section/CanDeleteSection.gql"),
                variables: {
                  sectionId: this.list.id
                }
              })
              if (!canDelete.data.canDeleteSection)
                return this.$toasted.error(
                  "Cannot archive list with incomplete tasks"
                )
              mutate()
            }
          },
          {
            label: "Cancel",
            color: "#444",
            ghost: true
          }
        ]
      })
    },
    /**
     *
     * @param value
     */
    onCollapse(value) {
      this.visible = value
      if (value) this.setcollapseAll(false)
    },
    /**
     *
     * @param sortProperty
     * @returns {this}
     */
    sortTasks(sortProperty) {
      function compareNumber(asc) {
        return function(a, b) {
          if (a.number > b.number) return asc ? 1 : -1
          if (b.number > a.number) return asc ? -1 : 1
          return 0
        }
      }

      /**
       *
       * @param asc
       * @returns {function(...[*]=)}
       */
      function compareHours(asc) {
        return function(a, b) {
          if (a.hours > b.hours) return asc ? 1 : -1
          if (b.hours > a.hours) return asc ? -1 : 1
          return 0
        }
      }

      /**
       *
       * @param asc
       * @returns {function(...[*]=)}
       */
      function compareDueDates(asc) {
        return function(a, b) {
          if (a.end > b.end) return asc ? 1 : -1
          if (b.end > a.end) return asc ? -1 : 1
          return 0
        }
      }

      /**
       *
       * @param asc
       * @returns {function(...[*]=)}
       */
      function comparePriority(asc) {
        return function(a, b) {
          if (
            (a.priority == "low" ? 1 : a.priority == "medium" ? 2 : 3) >
            (b.priority == "low" ? 1 : b.priority == "medium" ? 2 : 3)
          )
            return asc ? 1 : -1
          if (
            (b.priority == "low" ? 1 : b.priority == "medium" ? 2 : 3) >
            (a.priority == "low" ? 1 : a.priority == "medium" ? 2 : 3)
          )
            return asc ? -1 : 1
          return 0
        }
      }

      /**
       *
       * @param asc
       * @returns {function(...[*]=)}
       */
      function compareStatus(asc) {
        return function(a, b) {
          if (a.status > b.status) return asc ? 1 : -1
          if (b.status > a.status) return asc ? -1 : 1
          return 0
        }
      }

      switch (sortProperty) {
        case "hours":
          this.hourSort = !this.hourSort
          this.list.tasks.sort(compareHours(this.hourSort))
          break
        case "status":
          this.statusSort = !this.statusSort
          return this.list.tasks.sort(compareStatus(this.statusSort))
        case "priority":
          this.prioritySort = !this.prioritySort
          return this.list.tasks.sort(comparePriority(this.prioritySort))
        case "dueDate":
          this.prioritySort = !this.prioritySort
          return this.list.tasks.sort(compareDueDates(this.prioritySort))
        case "number":
          this.numberSort = !this.numberSort
          return this.list.tasks.sort(compareNumber(this.numberSort))
      }
    },
    /**
     *
     * @param oldIndex
     * @param newIndex
     * @returns {default.computed.tasks|[]|[]}
     */
    onTaskMove(oldIndex, newIndex) {
      while (oldIndex < 0) {
        oldIndex += this.list.tasks.length
      }
      while (newIndex < 0) {
        newIndex += this.list.tasks.length
      }
      this.list.tasks.splice(
        newIndex,
        0,
        this.list.tasks.splice(oldIndex, 1)[0]
      )
      return this.list.tasks
    }
  }
}
</script>
<style scoped>
.nav-icon {
  cursor: pointer;
}
input {
  border: none;
  width: 90%;
  background: transparent;
}
input:focus {
  outline: none;
}
.list-group {
  margin-bottom: -2px !important;
}
.totals {
  background-color: #dedede;
  border-color: #d2d2d2;
  margin-bottom: 20px !important;
  border-bottom-left-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
}
.totals p {
  margin: 0;
}
.totals [class*="col-"] {
  text-align: center;
}
.totals .hours {
  float: right;
  margin-right: 1rem;
}
.list-header {
  font-size: 1.01625rem;
  margin-bottom: 0.2rem;
}
.sort-grid {
  opacity: 0;
  transition: 0.3s;
}
.sort-grid:hover {
  opacity: 1;
}
.trash-grid {
  text-align: center;
  font-size: 14px;
  color: red;
  border: 1px solid;
  border-radius: 5px;
  background-color: #ffbdbd;
  padding: 7px;
}
.mb-20 {
  margin-bottom: 20px !important;
}

.highlight {
  background-color: #3f51b554;
  transition: background-color 1s linear;
}
.no-highlight {
  background-color: transparent;
}
.progress-text {
  font-size: 7px;
}
.progress {
  max-width: 200px;
  margin-top: 4px;
}
.total-pill {
  margin: 4px 0px 0px !important;
}
.box-date {
  margin-top: 0.5em;
  margin-bottom: 0.7em;
  display: flex;
  flex-direction: row;
  height: 1.7em;
  /* padding-bottom: 1em; */
  /* height: 95%; */
}
.i-Add {
  margin-right: 0.3rem;
  padding-top: 5px;
}
.fill {
  width: 6rem !important;
}
.sort-icons {
  display: flex;
  justify-content: space-between;
}

.no-add-task {
  margin-left: -3% !important;
  text-align: left;
}

.header-grid {
  display: grid;
  margin-left: 0.1em;
  margin-bottom: 0.3em;
  grid-template-areas: "add title ratio date";
  grid-template-columns: 0.1fr 3fr 1fr 1fr;
}

.addArea {
  grid-area: add;
}
.titleArea {
  grid-area: title;
  margin-left: 1em;
}
.ratioArea {
  grid-area: ratio;
  text-align: center;
  text-align: right;
}
.dateArea {
  grid-area: date;
  text-align: right;
  margin-right: 1em;
}

.sort-grid {
  display: grid;
  grid-template-areas: "numberSort hourSort dateSort statusSort prioritySort";
  grid-template-columns: 0.53fr 0.1fr 0.2fr 0.2fr 0.18fr;
}

.hourSort {
  grid-area: hourSort;
}
.dateSort {
  grid-area: dateSort;
}
.statusSort {
  grid-area: statusSort;
}
.numberSort {
  margin-left: 0.8rem;
  grid-area: numberSort;
}
.prioritySort {
  grid-area: prioritySort;
}
.due-col {
  text-align: right;
}
</style>
